import React, { useCallback } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { prepareProps } from 'record-it-components/utils/propUtils';
import {
  SearchableSelect,
  SearchableSelectProps
} from 'record-it-components/input/SearchableSelect/SearchableSelect';
import styles from './MonthSelect.module.css';
import { allMonths, Month } from './Month';

export type MonthSelectProps<T extends MonthSelectOption> = Omit<
  SearchableSelectProps<T>,
  | 'label'
  | 'items'
  | 'getDisplayTextOfItem'
  | 'getKeyOfItem'
  | 'value'
  | 'onValueChanged'
  | 'className'
> & {
  /**
   * Month selected by default.
   */
  readonly month: T;
  /**
   * Months the user can select from.
   */
  readonly availableMonths: Array<T>;
  /**
   * Called whenever the user selects a new month.
   */
  readonly onMonthSelected: (selectedMonth: T) => void;
};

export const MonthSelect = React.memo(
  <T extends MonthSelectOption>(props: MonthSelectProps<T>) => {
    const { month, availableMonths, onMonthSelected, ...remainingProps } =
      prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();
    const getDisplayTextOfMonth = useCallback(
      (mon: T) => t(monthToTk[mon]).toString(),
      [t]
    );
    const getKeyOfMonth = useCallback((mon: T) => String(mon), []);

    return (
      <SearchableSelect<T>
        {...remainingProps}
        label={t('components.input.MonthSelect.label')}
        items={availableMonths}
        getDisplayTextOfItem={getDisplayTextOfMonth}
        getKeyOfItem={getKeyOfMonth}
        value={month}
        onValueChanged={onMonthSelected}
        className={styles.monthSelect}
        data-testid="MonthSelect"
      ></SearchableSelect>
    );
  }
) as <T extends MonthSelectOption = Month>(
  props: MonthSelectProps<T>
) => JSX.Element;

/**
 * Represents "All Months in a given year".
 * Useful if you want to use the MonthSelect paired with a ExportYearSelect,
 * and would like that the user could either select a specific month in a year or the whole year.
 */
export const allMonthsOption = Symbol('allMonths');

export type MonthSelectOption = Month | typeof allMonthsOption;

/**
 * An array of all months in a year, including the "All" option.
 */
export const allMonthsInYearIncludingAny: MonthSelectOption[] = [
  ...allMonths,
  allMonthsOption
];

/**
 * Mapping from a month to its translation string.
 */
export const monthToTk: Record<
  MonthSelectOption,
  Parameters<TFunction<'time', undefined>>[0]
> = {
  [allMonthsOption]: 'date.monthLong.allMonths',
  [Month.January]: 'date.monthLong.january',
  [Month.February]: 'date.monthLong.february',
  [Month.March]: 'date.monthLong.march',
  [Month.April]: 'date.monthLong.april',
  [Month.May]: 'date.monthLong.may',
  [Month.June]: 'date.monthLong.juni',
  [Month.July]: 'date.monthLong.july',
  [Month.August]: 'date.monthLong.august',
  [Month.September]: 'date.monthLong.september',
  [Month.October]: 'date.monthLong.october',
  [Month.November]: 'date.monthLong.november',
  [Month.December]: 'date.monthLong.december'
};
